import {
    Alert,
    Box,
    Button,
    Chip,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';

import { ContextStore } from '../store/contextStore';
import { useDeveloperApi } from './api';
import {
    CodeBlock,
    CopyButton,
    describeError,
    DetailDrawer,
    DeveloperShell,
    ErrorAlert,
    Field,
    LoadMore,
    Loading,
    mono,
    summaryText,
    TableScroll,
    Timestamp,
    ToneChip,
} from './components';
import { bookingOutcome, REQUEST_STATES, requestState, stateReason } from './labels';
import { humanizeKey } from './integrations/integrationLabels';
import { DEVELOPER_PATHS, importRunPath } from './routes';
import type { BodyView, DeveloperRequestDetail, RequestLogItem } from './types';
import { useCursorList } from './useCursorList';

const STATUS_PATTERN = /^([1-5][0-9]{2}|[1-5]xx)$/;

const StateCell = ({ state, reason }: { state: string; reason: string | null }) => {
    const label = requestState(state);
    return (
        <Stack spacing={0.25} alignItems="flex-start">
            <ToneChip label={label.label} tone={label.tone} hint={label.hint} />
            {reason && <Typography variant="caption" color="text.secondary">{stateReason(reason)}</Typography>}
        </Stack>
    );
};

const Body = ({ title, view, testId }: { title: string; view: BodyView | null; testId: string }) => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {title}
            {view?.content_type ? <Typography component="span" variant="caption" color="text.secondary"> {view.content_type}</Typography> : null}
        </Typography>
        {!view ? (
            <Typography variant="body2" color="text.secondary">The backend never answered.</Typography>
        ) : !view.available ? (
            <Typography variant="body2" color="text.secondary">No longer kept.</Typography>
        ) : (
            <>
                <CodeBlock text={view.body} testId={testId} />
                {view.truncated && <Typography variant="caption" color="text.secondary">Cut at 64 KB.</Typography>}
            </>
        )}
    </Box>
);

const RequestDetail = ({ requestId, onReplayed }: { requestId: string; onReplayed: () => void }) => {
    const api = useDeveloperApi();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, environment } = React.useContext(ContextStore) as any;
    const [detail, setDetail] = React.useState<DeveloperRequestDetail | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [replaying, setReplaying] = React.useState(false);
    const [replayError, setReplayError] = React.useState<unknown>(null);

    const load = React.useCallback(() => {
        setError(null);
        api.request(requestId).then(setDetail).catch(setError);
    }, [api, requestId]);

    React.useEffect(() => {
        setDetail(null);
        load();
    }, [load]);

    if (error) {
        return <ErrorAlert error={error} onRetry={load} />;
    }
    if (!detail) {
        return <Loading />;
    }

    const canReplay = detail.replayable && detail.tenant.environment === 'sandbox' && environment === 'sandbox'
        && hasPerm?.('developer', 'outbound_manage');
    const replay = async () => {
        setReplaying(true);
        setReplayError(null);
        try {
            await api.replay(detail.request_id);
            enqueueSnackbar('Queued for delivery again', { variant: 'success' });
            load();
            onReplayed();
        } catch (e) {
            setReplayError(e);
        } finally {
            setReplaying(false);
        }
    };

    return (
        <Box data-testid="request-detail">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                <StateCell state={detail.state} reason={detail.state_reason} />
                {detail.mirror_of && <Chip size="small" variant="outlined" label="Mirrored copy" />}
                <Box sx={{ flexGrow: 1 }} />
                {canReplay && (
                    <Button variant="outlined" size="small" onClick={replay} disabled={replaying}>Replay</Button>
                )}
            </Stack>
            {replayError ? <Alert severity="error" sx={{ mb: 1 }}>{describeError(replayError)}</Alert> : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Field label="Received"><Timestamp value={detail.received_at} /></Field>
                <Field label="Finished"><Timestamp value={detail.finished_at} /></Field>
                <Field label="Request"><span style={mono}>{detail.method} {detail.path}</span></Field>
                <Field label="Endpoint">{detail.endpoint || '—'}</Field>
                <Field label="Integration">{detail.backend?.integration || '—'}</Field>
                <Field label="API key">
                    {detail.backend?.credential ? `${detail.backend.credential.name} (${detail.backend.credential.key_prefix}…)` : '—'}
                </Field>
                {detail.edge && (
                    <Field label="Edge">
                        {detail.edge.status}, {detail.edge.attempts} {detail.edge.attempts === 1 ? 'delivery' : 'deliveries'}
                        {detail.edge.answer_status ? `, answered ${detail.edge.answer_status}` : ''}
                    </Field>
                )}
                {detail.backend && (
                    <Field label="Backend answer">
                        {detail.backend.status_code || 'still answering'}{detail.backend.duration_ms != null ? ` in ${detail.backend.duration_ms} ms` : ''}
                        {detail.backend.error ? ` (${detail.backend.error})` : ''}
                    </Field>
                )}
                {detail.mirror_of && <Field label="Copy of production request"><span style={mono}>{detail.mirror_of}</span></Field>}
                {detail.import_run && (
                    <Field label="Import">
                        <RouterLink to={importRunPath(detail.import_run.id)}>{detail.import_run.state}</RouterLink>
                    </Field>
                )}
                <Field label="Status URL">
                    <Stack direction="row" alignItems="center"><span style={mono}>{detail.status_url}</span><CopyButton value={detail.status_url} label="status URL" /></Stack>
                </Field>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
                Bookings {summaryText(detail.summary) ? `(${summaryText(detail.summary)})` : ''}
            </Typography>
            {detail.outcomes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No bookings in this request.</Typography>
            ) : (
                <TableScroll>
                    <Table size="small" data-testid="request-outcomes">
                        <TableHead>
                            <TableRow>
                                <TableCell>Booking</TableCell>
                                <TableCell>Outcome</TableCell>
                                <TableCell>Why</TableCell>
                                <TableCell>Booking id</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detail.outcomes.map((o, idx) => {
                                const label = bookingOutcome(o.outcome);
                                return (
                                    <TableRow key={`${o.booking_ref}-${idx}`}>
                                        <TableCell sx={mono}>{o.booking_ref}</TableCell>
                                        <TableCell><ToneChip label={label.label} tone={label.tone} /></TableCell>
                                        <TableCell>{o.reason || o.code || '—'}</TableCell>
                                        <TableCell>{o.tour_user_id ?? '—'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <Body title="Request body" view={detail.request} testId="request-body" />
            <Body title="Response body" view={detail.response} testId="response-body" />
        </Box>
    );
};

/** The request log (SBX D9): every inbound call, the edge's refusals and mirrored copies included,
 *  with each booking's outcome. `/developer/requests/<request id>` opens one request. */
const RequestLogPage = () => {
    const api = useDeveloperApi();
    const history = useHistory();
    const { requestId } = useParams<{ requestId?: string }>();
    // Developer › Integrations' fact tiles link here filtered (`?integration=ventrata&status=4xx`).
    const location = useLocation();
    const [linked] = React.useState(() => {
        const query = new URLSearchParams(location.search);
        const linkedStatus = (query.get('status') || '').trim();
        return {
            integration: (query.get('integration') || '').trim(),
            // The system's name for the chip (Integrations passes it; a bare code reads as a word).
            integrationName: (query.get('integration_name') || '').trim(),
            status: STATUS_PATTERN.test(linkedStatus) ? linkedStatus : '',
        };
    });
    const [state, setState] = React.useState('');
    const [integration, setIntegration] = React.useState(linked.integration);
    const [status, setStatus] = React.useState(linked.status);
    const [bookingRef, setBookingRef] = React.useState('');
    const [searchId, setSearchId] = React.useState('');
    const [applied, setApplied] = React.useState({ status: linked.status, booking_ref: '', request_id: '' });
    const statusInvalid = !!status && !STATUS_PATTERN.test(status.trim());

    const list = useCursorList<RequestLogItem>(api.requests, { state, integration, ...applied });

    const apply = (ev?: React.FormEvent) => {
        ev?.preventDefault();
        if (statusInvalid) {
            return;
        }
        setApplied({ status: status.trim(), booking_ref: bookingRef.trim(), request_id: searchId.trim() });
    };

    return (
        <DeveloperShell title="Request log">
            <Box component="form" onSubmit={apply} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1.5 }}>
                <TextField select size="small" label="State" value={state} onChange={(ev) => setState(ev.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(REQUEST_STATES).map(([value, { label }]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField
                    size="small"
                    label="HTTP status"
                    placeholder="429 or 4xx"
                    value={status}
                    onChange={(ev) => setStatus(ev.target.value)}
                    error={statusInvalid}
                    helperText={statusInvalid ? 'A status (429) or a class (4xx)' : undefined}
                    sx={{ width: 140 }}
                />
                <TextField size="small" label="Booking reference" value={bookingRef} onChange={(ev) => setBookingRef(ev.target.value)} />
                <TextField size="small" label="Request id" value={searchId} onChange={(ev) => setSearchId(ev.target.value)} sx={{ minWidth: 280 }} />
                <Button type="submit" variant="outlined" disabled={statusInvalid}>Search</Button>
                {integration && (
                    <Chip
                        label={`Booking system: ${linked.integrationName || humanizeKey(integration)}`}
                        onDelete={() => setIntegration('')}
                        data-testid="integration-filter"
                        sx={{ alignSelf: 'center' }}
                    />
                )}
            </Box>
            {list.error ? <ErrorAlert error={list.error} onRetry={list.reload} /> : null}
            {list.loading && list.items.length === 0 ? <Loading /> : (
                <TableScroll>
                    <Table size="small" data-testid="request-log">
                        <TableHead>
                            <TableRow>
                                <TableCell>Received</TableCell>
                                <TableCell>Request</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>API key</TableCell>
                                <TableCell>Bookings</TableCell>
                                <TableCell align="right">Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((row) => (
                                <TableRow
                                    key={row.request_id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`${DEVELOPER_PATHS.requests}/${row.request_id}`)}
                                    data-testid={`request-row-${row.request_id}`}
                                >
                                    <TableCell><Timestamp value={row.received_at} /></TableCell>
                                    <TableCell sx={{ maxWidth: 360 }}>
                                        <Typography sx={mono}>{row.method} {row.path}</Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                            {row.integration && <Typography variant="caption" color="text.secondary">{row.integration}</Typography>}
                                            {row.mirror_of && <Chip size="small" variant="outlined" label="Mirror" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell><StateCell state={row.state} reason={row.state_reason} /></TableCell>
                                    <TableCell>{row.status_code ?? '—'}</TableCell>
                                    <TableCell>{row.credential ? `${row.credential.name} (${row.credential.key_prefix}…)` : '—'}</TableCell>
                                    <TableCell>
                                        {summaryText(row.summary) || '—'}
                                        {row.import_run_id && (
                                            <Box>
                                                <RouterLink to={importRunPath(row.import_run_id)} onClick={(ev) => ev.stopPropagation()}>Import</RouterLink>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{row.duration_ms != null ? `${row.duration_ms} ms` : '—'}</TableCell>
                                </TableRow>
                            ))}
                            {!list.loading && list.items.length === 0 && !list.error && (
                                <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No requests match.</Typography></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
            <DetailDrawer
                open={!!requestId}
                onClose={() => history.push(DEVELOPER_PATHS.requests)}
                title={<Stack direction="row" alignItems="center" spacing={0.5}><Typography sx={{ ...mono, fontSize: 15 }}>{requestId}</Typography><CopyButton value={requestId} label="request id" /></Stack>}
            >
                {requestId && <RequestDetail requestId={requestId} onReplayed={list.reload} />}
            </DetailDrawer>
        </DeveloperShell>
    );
};

export default RequestLogPage;
